/**
 * ZToolFolders - Zod Validation Schemas for Tool Folders API
 *
 * Endpoints: /api/tools/folders/*
 * Controller: tools/tools-folders.controller.ts
 *
 * The assign route is /assign-tool (not /assign-item) for legacy-path parity.
 * UpdateIcon is multipart/form-data (Body typed as FormData for the generated
 * client; the backend reads the file via FileInterceptor).
 */

import { z } from "zod";

import {
  CreateFolderBodySchema,
  FolderIconRemovedSchema,
  FolderIconUpdatedSchema,
  FolderMutationOkSchema,
  FolderPathVarsSchema,
  FolderSchema,
  UpdateFolderBodySchema
} from "../../common/folder.schema";
import { ApiSuccessSchema } from "../../common/response.schema";

// ═══════════════════════════════════════════════════════════════
// SHARED SCHEMAS
// ═══════════════════════════════════════════════════════════════

const ToolFolderAssignmentSchema = z.object({
  toolId: z.string(),
  folderId: z.string()
});

export type ToolFolderAssignment = z.infer<typeof ToolFolderAssignmentSchema>;

const ToolFoldersGetAllResponseSchema = z.object({
  folders: z.array(FolderSchema),
  assignments: z.array(ToolFolderAssignmentSchema)
});

const AssignToolBodySchema = z.object({
  // A "tool" here is a Workflow, an AITask or an AgentExternalTool row — the merged
  // skills feed the tool grid renders — and all three key on a uuid column. The
  // marketplace catalog is NOT in this feature: connecting a marketplace tool creates
  // an AgentExternalTool row first, and only that row's uuid ever reaches a folder,
  // so no slug can arrive here. The assignment table storing it is a bare String with
  // no foreign key, so an unvalidated id persists pointing at nothing. The folderId
  // sibling DOES have a foreign key, so a bad value there surfaces as a Prisma error
  // — a 500 where a 400 belongs. Only the body is tightened; the response schema
  // still describes rows written before this check existed.
  toolId: z.string().uuid(),
  folderId: z.string().uuid().nullable()
});

// ═══════════════════════════════════════════════════════════════
// ZTOOL FOLDERS MANIFEST
// ═══════════════════════════════════════════════════════════════

export const ZToolFolders = {
  /** GET /api/tools/folders — all folders + tool assignments. */
  GetAll: {
    method: "GET" as const,
    path: "/api/tools/folders",
    Response: ApiSuccessSchema(ToolFoldersGetAllResponseSchema)
  },

  /** POST /api/tools/folders — create a folder. */
  Create: {
    method: "POST" as const,
    path: "/api/tools/folders",
    Body: CreateFolderBodySchema,
    Response: ApiSuccessSchema(FolderSchema)
  },

  /** PATCH /api/tools/folders/:id — rename or move a folder. */
  Update: {
    method: "PATCH" as const,
    path: "/api/tools/folders/:id",
    PathVars: FolderPathVarsSchema,
    Body: UpdateFolderBodySchema,
    Response: ApiSuccessSchema(FolderSchema)
  },

  /** POST /api/tools/folders/:id/updateIcon — multipart icon upload ("file" field). */
  UpdateIcon: {
    method: "POST" as const,
    path: "/api/tools/folders/:id/updateIcon",
    PathVars: FolderPathVarsSchema,
    Body: z.custom<FormData>(),
    Response: ApiSuccessSchema(FolderIconUpdatedSchema)
  },

  /** POST /api/tools/folders/:id/removeIcon — remove the folder icon. */
  RemoveIcon: {
    method: "POST" as const,
    path: "/api/tools/folders/:id/removeIcon",
    PathVars: FolderPathVarsSchema,
    Response: ApiSuccessSchema(FolderIconRemovedSchema)
  },

  /** DELETE /api/tools/folders/:id — delete a folder. */
  Delete: {
    method: "DELETE" as const,
    path: "/api/tools/folders/:id",
    PathVars: FolderPathVarsSchema,
    Response: ApiSuccessSchema(FolderMutationOkSchema)
  },

  /** POST /api/tools/folders/assign-tool — assign a tool (folderId null = root). */
  AssignItem: {
    method: "POST" as const,
    path: "/api/tools/folders/assign-tool",
    Body: AssignToolBodySchema,
    Response: ApiSuccessSchema(FolderMutationOkSchema)
  }
} as const;
